import { EventEmitter, Injectable, inject } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Observable, Subject, timer } from "rxjs";
import { ObjectUtils } from "../utils/object-utils";
import { IValidationScope } from "../validation/IValidationScope";
import { AuthenticationService } from "./authentication/authentication.service";

@Injectable({
    providedIn: "root"
})
export class FormBinderService {
    private readonly fb = inject(UntypedFormBuilder);
    private readonly authenticationService = inject(AuthenticationService);

    private readonly protectedFields = [
        "id",
        "rowVersion"
    ];

    CreateGroup(modelToBind: any, fields?: string[], eventEmitter?: EventEmitter<any>, checkAuthentication = true): UntypedFormGroup {
        const config = this.createBindingConfig(modelToBind);
        const group = this.fb.group(config);
        this.bindForm(group, modelToBind, fields, eventEmitter);
        if (checkAuthentication) {
            this.checkAuthentication(group);
        }
        return group;
    }

    attachValidation(validation: IValidationScope, form: UntypedFormGroup, runOnAttach = false) {
        if (runOnAttach) {
            setTimeout(() => {
                this.updateErrors(validation, form);
            });
        }
        form.valueChanges.subscribe(() => {
            this.updateErrors(validation, form);
        });
    }

    updateErrors(validation: IValidationScope, form: UntypedFormGroup) {
        form.setErrors(null);
        const ruleErrors = validation.rules.filter(r => r.hasError).map(r => r.name);
        if (ruleErrors.length === 0) {
            return;
        }

        const error = {};
        ruleErrors.forEach(r => error[r] = true);
        form.setErrors(error);
    }

    attachToRootForm(rootForm: UntypedFormGroup, form: UntypedFormGroup, formName: string): Observable<any> {
        const subject = new Subject<void>();
        timer(0).subscribe(() => {
            this.detachFromRootForm(rootForm, formName);
            rootForm.addControl(formName, form);
            subject.next();
            subject.complete();
        });
        return subject.asObservable();
    }

    detachFromRootForm(rootForm: UntypedFormGroup, formName: string): void {
        rootForm.removeControl(formName);
    }

    updateFromModel(group: UntypedFormGroup, model, emitChangeEvent = false) {
        const properties = Object.keys(model);
        properties.forEach(propertyName => {
            const control = group.controls[propertyName];
            if (control && control.value !== model[propertyName]) {
                control.setValue(model[propertyName], { emitEvent: emitChangeEvent });
            }
        });
    }

    reBindForm(group: UntypedFormGroup, model, emitChangeEvent = false, pristine = true) {
        if (!ObjectUtils.hasValue(group) || !ObjectUtils.hasValue(model)) {
            return;
        }
        this.updateFromModel(group, model, emitChangeEvent);
        this.bindForm(group, model, null, null);
        if (pristine) {
            group.markAsPristine();
        }
    }

    private checkAuthentication(formGroup: UntypedFormGroup) {
        if (!this.authenticationService.canEdit()) {
            formGroup.disable();
        }
    }

    private bindForm(form: UntypedFormGroup, model: any, fields: string[], eventEmitter: EventEmitter<any>) {
        form.valueChanges.subscribe((formModel) => {
            let updated = false;
            for (const propertyName in formModel) {
                if (this.shouldUpdate(model[propertyName], formModel[propertyName], propertyName, fields)) {
                    model[propertyName] = formModel[propertyName];
                    updated = true;
                }
            }
            if (eventEmitter && updated) {
                eventEmitter.emit(model);
            }
        });
    }

    private createBindingConfig(modelToBind: any) {
        const config = {};
        for (const key in modelToBind) {
            if (Object.hasOwn(modelToBind, key)) {
                const element = this.parseElement(modelToBind[key]);
                config[key] = [element];
            }
        }
        return config;
    }

    private parseElement(element: any) {
        if (Array.isArray(element)) {
            element = element.map(e => e);
        }
        return element;
    }

    private shouldUpdate(oldValue, newValue, propertyName: string, fields: string[]): boolean {
        if (this.protectedFields.indexOf(propertyName) > -1 || !!fields && fields.indexOf(propertyName) === -1) {
            return false;
        }

        return oldValue !== newValue;
    }
}
